import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { changeEmailSchema, changePasswordSchema, type ChangeEmailInput, type ChangePasswordInput, type LinkedIdentity, type MeResponse } from '@rw/shared';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Page } from '../../components/Layout';
import { Button, Card } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { useAuth } from '../../stores/auth';
import { useNetwork } from '../../stores/network';
import { toast } from '../../stores/toasts';
import { t as tr, useLang } from '../../i18n';
import { isInAppBrowser } from '../../lib/in-app-browser';
import { GOOGLE_BUTTON_STYLE, useGoogleButton } from '../auth/google';
import { OpenInBrowserNote } from '../auth/SocialButtons';

const input = 'mt-1 h-12 w-full rounded-xl border border-ink-700 bg-ink-850 px-4 outline-none focus:border-volt';

/** Change the sign-in email, and change (or, for Strava/Google/Facebook accounts, set) a password. */
export function AccountPage() {
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  useLang((s) => s.lang);
  if (!user) return null;
  return (
    <Page>
      <button onClick={() => navigate(-1)} className="mb-2 text-sm font-semibold text-ink-300">
        {tr('‹ Back')}
      </button>
      <h1 className="font-display text-3xl font-bold">{tr('Email & password')}</h1>
      <EmailCard user={user} />
      <PasswordCard user={user} />
      <GoogleCard user={user} />
    </Page>
  );
}

function useSaveMe() {
  const qc = useQueryClient();
  const setUser = useAuth((s) => s.setUser);
  return (u: MeResponse) => {
    qc.setQueryData(['me', 'profile'], u);
    setUser(u);
  };
}

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : tr('Something went wrong'));

function EmailCard({ user }: { user: MeResponse }) {
  const online = useNetwork((s) => s.online);
  const saveMe = useSaveMe();
  const current = user.hasEmail === false ? '' : user.email;
  const [email, setEmail] = useState(current);
  const [password, setPassword] = useState('');
  const body: ChangeEmailInput = { email, ...(user.hasPassword ? { currentPassword: password } : {}) };
  const valid = changeEmailSchema.safeParse(body).success && email.trim().toLowerCase() !== current && (!user.hasPassword || password.length > 0);

  const save = useMutation({
    mutationFn: () => api<MeResponse>('/auth/email', { method: 'PATCH', body }),
    onSuccess: (u) => {
      saveMe(u);
      setPassword('');
      toast({ kind: 'success', title: tr('Email updated'), body: tr('Use it to sign in from now on.') });
    },
  });

  return (
    <Card className="mt-5">
      <h2 className="font-display text-xl font-bold">{tr('Email')}</h2>
      {user.hasEmail === false ? (
        <p className="mt-1 text-xs text-ink-400">{tr('Your account has no email yet (you sign in with Strava, Google or Facebook). Add one to also sign in with a password.')}</p>
      ) : null}
      <label className="mt-3 block text-sm font-semibold">
        {tr('Email')}
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" className={input} data-testid="email-input" />
      </label>
      {user.hasPassword ? (
        <label className="mt-3 block text-sm font-semibold">
          {tr('Current password')}
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            className={input}
            data-testid="email-current-password"
          />
        </label>
      ) : null}
      {save.isError ? <p className="mt-2 text-xs text-enemy">{errorText(save.error)}</p> : null}
      <Button className="mt-4 w-full" disabled={!valid || !online} loading={save.isPending} onClick={() => save.mutate()} data-testid="save-email">
        {tr('Save email')}
      </Button>
    </Card>
  );
}

function PasswordCard({ user }: { user: MeResponse }) {
  const online = useNetwork((s) => s.online);
  const saveMe = useSaveMe();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const body: ChangePasswordInput = { newPassword: next, ...(user.hasPassword ? { currentPassword: current } : {}) };
  const parsed = changePasswordSchema.safeParse(body);
  const mismatch = confirm.length > 0 && confirm !== next;
  const tooShort = next.length > 0 && !parsed.success ? parsed.error.issues.find((i) => i.path[0] === 'newPassword')?.message : undefined;
  const canSet = user.hasPassword || user.hasEmail !== false;
  const valid = parsed.success && confirm === next && (!user.hasPassword || current.length > 0) && canSet;

  const save = useMutation({
    mutationFn: () => api<MeResponse>('/auth/password', { method: 'PATCH', body }),
    onSuccess: (u) => {
      saveMe(u);
      setCurrent('');
      setNext('');
      setConfirm('');
      toast({ kind: 'success', title: tr(user.hasPassword ? 'Password changed' : 'Password set'), body: tr('Other devices were signed out.') });
    },
  });

  return (
    <Card className="mt-4">
      <h2 className="font-display text-xl font-bold">{tr(user.hasPassword ? 'Change password' : 'Set a password')}</h2>
      {!canSet ? <p className="mt-1 text-xs text-ink-400">{tr('Add your email above first, then set a password.')}</p> : null}
      {user.hasPassword ? (
        <label className="mt-3 block text-sm font-semibold">
          {tr('Current password')}
          <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} autoComplete="current-password" className={input} data-testid="current-password" />
        </label>
      ) : null}
      <label className="mt-3 block text-sm font-semibold">
        {tr('New password')}
        <input type="password" value={next} onChange={(e) => setNext(e.target.value)} autoComplete="new-password" disabled={!canSet} className={input} data-testid="new-password" />
      </label>
      {tooShort ? <p className="mt-1 text-xs text-enemy">{tr(tooShort)}</p> : null}
      <label className="mt-3 block text-sm font-semibold">
        {tr('Repeat new password')}
        <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" disabled={!canSet} className={input} data-testid="confirm-password" />
      </label>
      {mismatch ? <p className="mt-1 text-xs text-enemy">{tr('Passwords don’t match')}</p> : null}
      {save.isError ? <p className="mt-2 text-xs text-enemy">{errorText(save.error)}</p> : null}
      <Button className="mt-4 w-full" disabled={!valid || !online} loading={save.isPending} onClick={() => save.mutate()} data-testid="save-password">
        {tr(user.hasPassword ? 'Change password' : 'Set password')}
      </Button>
    </Card>
  );
}

/** Link Google to this account (then either way of signing in opens it), or unlink it. */
function GoogleCard({ user }: { user: MeResponse }) {
  const online = useNetwork((s) => s.online);
  const saveMe = useSaveMe();
  const qc = useQueryClient();
  const el = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const providers = useQuery({
    queryKey: ['auth-providers'],
    queryFn: () => api<{ google: { clientId: string } | null }>('/auth/providers'),
    staleTime: 10 * 60_000,
  });
  const identities = useQuery({
    queryKey: ['auth-identities'],
    queryFn: () => api<LinkedIdentity[]>('/auth/identities'),
  });
  const google = identities.data?.find((i) => i.provider === 'GOOGLE');
  const clientId = providers.data?.google?.clientId;
  const inApp = isInAppBrowser();

  const link = useMutation({
    mutationFn: (credential: string) =>
      api<MeResponse>('/auth/identities/google', { body: { credential } }),
    onSuccess: (u) => {
      saveMe(u);
      setError(null);
      void qc.invalidateQueries({ queryKey: ['auth-identities'] });
      toast({
        kind: 'success',
        title: tr('Google linked'),
        body: tr('You can now sign in with Google too.'),
      });
    },
    onError: (e) => setError(errorText(e)),
  });
  const unlink = useMutation({
    mutationFn: () => api<void>('/auth/identities/google', { method: 'DELETE' }),
    onSuccess: () => {
      setError(null);
      void qc.invalidateQueries({ queryKey: ['auth-identities'] });
      toast({ kind: 'success', title: tr('Google unlinked') });
    },
    onError: (e) => setError(errorText(e)),
  });
  useGoogleButton(
    el,
    !inApp && identities.isSuccess && !google ? clientId : null,
    (credential) => link.mutate(credential),
    () => setError(tr('Google sign-in is unavailable right now')),
    'signin_with',
  );

  if (!clientId || !identities.isSuccess) return null;
  return (
    <div data-testid="google-card">
      <Card className="mt-4">
        <h2 className="font-display text-xl font-bold">Google</h2>
        {google ? (
          <>
            <p className="mt-1 text-sm text-ink-300" data-testid="google-linked">
              {tr('Linked to {email}. You can sign in with Google or as before.', {
                email: google.email ?? 'Google',
              })}
            </p>
            <Button
              variant="secondary"
              className="mt-4 w-full"
              disabled={!online || (!user.hasPassword && identities.data.length < 2)}
              loading={unlink.isPending}
              onClick={() => unlink.mutate()}
              data-testid="google-unlink"
            >
              {tr('Unlink Google')}
            </Button>
            {!user.hasPassword && identities.data.length < 2 ? (
              <p className="mt-2 text-xs text-ink-400">
                {tr('Set a password first: this is your only way to sign in')}
              </p>
            ) : null}
          </>
        ) : (
          <>
            <p className="mt-1 text-sm text-ink-300">
              {tr('Link your Google account to sign in with one tap.')}
            </p>
            <div className="mt-3">
              {inApp ? (
                <OpenInBrowserNote />
              ) : (
                <div ref={el} className="flex justify-center" style={GOOGLE_BUTTON_STYLE} data-testid="google-link" />
              )}
            </div>
          </>
        )}
        {error ? (
          <p className="mt-2 text-xs text-enemy" data-testid="google-error">
            {error}
          </p>
        ) : null}
      </Card>
    </div>
  );
}
